/**
 * Help Page - Desktop Version
 *
 * Main help page for desktop users with manual selection and content display.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DesktopLayout from '../../components/layout/DesktopLayout';
import { ManualContent } from '../../components/help/ManualContent';
import { ManualSidebar } from '../../components/help/ManualSidebar';
import { MANUALS_METADATA, getAvailableManuals } from '../../data/manuals';
import type { TableOfContentItem, ManualMetadata } from '../../types/manual';
import { useAuth } from '../../contexts/AuthContext';
import './HelpPage.css';

export const HelpPage = () => {
  const { user } = useAuth();
  const { manualId } = useParams<{ manualId?: string }>();
  const navigate = useNavigate();

  const [selectedManual, setSelectedManual] = useState<ManualMetadata | null>(null);
  const [content, setContent] = useState<string>('');
  const [tableOfContents, setTableOfContents] = useState<TableOfContentItem[]>([]);
  const [activeHeadingId, setActiveHeadingId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const availableManuals = getAvailableManuals(user?.role || 'member');

  // Initialize selected manual from URL or default
  useEffect(() => {
    if (availableManuals.length === 0) {
      setError('沒有可用的操作手冊');
      setLoading(false);
      return;
    }

    const manualToUse = manualId
      ? MANUALS_METADATA.find((m) => m.id === manualId)
      : availableManuals[0];

    if (!manualToUse || !availableManuals.find((m) => m.id === manualToUse.id)) {
      // Redirect to first available manual if invalid or unauthorized
      navigate(`/help/${availableManuals[0].id}`, { replace: true });
      return;
    }

    setSelectedManual(manualToUse);
  }, [manualId, availableManuals, navigate]);

  // Load manual content
  useEffect(() => {
    if (!selectedManual) return;

    const loadManual = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(selectedManual.path);

        if (!response.ok) {
          throw new Error('無法載入手冊內容');
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : '載入失敗');
        setContent('');
      } finally {
        setLoading(false);
      }
    };

    loadManual();
  }, [selectedManual]);

  // Track active heading
  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll('[id^="heading-"]');
      const scrollPosition = window.scrollY + 150;

      let currentHeading = '';

      headings.forEach((heading) => {
        if (heading instanceof HTMLElement) {
          const headingTop = heading.offsetTop;

          if (headingTop <= scrollPosition) {
            currentHeading = heading.id;
          }
        }
      });

      setActiveHeadingId(currentHeading);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [content]);

  const handleManualChange = (manualId: string) => {
    navigate(`/help/${manualId}`);
  };

  if (loading) {
    return (
      <div className="help-page">
        <DesktopLayout>
        <main className="help-page__main">
          <div className="help-page__loading">載入中...</div>
        </main>
        </DesktopLayout>
      </div>
    );
  }

  if (error) {
    return (
      <div className="help-page">
        <DesktopLayout>
        <main className="help-page__main">
          <div className="help-page__error">
            <p>⚠️ {error}</p>
          </div>
        </main>
        </DesktopLayout>
      </div>
    );
  }

  if (!selectedManual) {
    return null;
  }

  return (
    <div className="help-page">
      <DesktopLayout>
      <main className="help-page__main">
        <div className="help-page__container">
          {/* Page Header */}
          <header className="help-page__header">
            <h1 className="help-page__title">操作手冊</h1>
          </header>

          {/* Manual Selector */}
          <div className="help-page__selector">
            <div className="help-page__tabs">
              {availableManuals.map((manual) => (
                <button
                  key={manual.id}
                  className={`help-page__tab ${
                    selectedManual.id === manual.id ? 'help-page__tab--active' : ''
                  }`}
                  onClick={() => handleManualChange(manual.id)}
                >
                  <span className="help-page__tab-icon">{manual.icon}</span>
                  <span className="help-page__tab-title">{manual.title}</span>
                  {manual.version && (
                    <span className="help-page__tab-version">{manual.version}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="help-page__content">
            <ManualSidebar
              tableOfContents={tableOfContents}
              activeHeadingId={activeHeadingId}
            />
            <div className="help-page__content-area">
              <ManualContent
                content={content}
                onTableOfContentsExtracted={setTableOfContents}
                basePath={selectedManual.path}
              />
            </div>
          </div>
        </div>
      </main>
      </DesktopLayout>
    </div>
  );
};

export default HelpPage;
